// The Starter Tape studio: transport bar, instrument lanes and a piano roll
// for the selected lane. Shared by the tapes and the sandbox; the page owns
// the song and the player (useTapePlayer) and passes both in.

import React, { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import {
  INSTRUMENTS,
  loopSeconds,
  noteAt,
  setBpm,
  clearTrack,
  setMuted,
  setNoteLength,
  setSound,
  toggleNote,
  totalSteps,
  trackRows,
} from "../../lib/starterTape";
import PianoRoll from "./PianoRoll";
import SoundPanel from "./SoundPanel";
import LaneList, { LANE_STYLES } from "./LaneList";

// Note lengths a click can place, in steps (a step is an eighth note).
const LENGTHS = [
  { steps: 1, label: "⅛" },
  { steps: 2, label: "¼" },
  { steps: 4, label: "½" },
  { steps: 8, label: "1 bar" },
  { steps: 16, label: "2 bars" },
];

export default function Studio({
  song,
  setSong,
  player,
  showTempo = true,
  transportExtras = null,
  beforeLanes = null,
  soundControls = [],
}) {
  const [selectedId, setSelectedId] = useState(
    song.tracks[song.tracks.length - 1].id
  );
  const track = song.tracks.find((t) => t.id === selectedId) || song.tracks[0];
  const inst = INSTRUMENTS[track.instrument];
  const [length, setLength] = useState(inst.defaultLength);
  const steps = totalSteps(song);
  const lengths = LENGTHS.filter((l) => l.steps <= steps);

  const { playing, waking, error, togglePlay, preview, registerPlayhead } =
    player;

  // Space bar plays/stops (unless a control has focus and wants the key).
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space") return;
      const tag = e.target.tagName;
      if (tag === "BUTTON" || tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      togglePlay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePlay]);

  const selectLane = (id) => {
    const next = song.tracks.find((t) => t.id === id);
    setSelectedId(id);
    if (next) setLength(INSTRUMENTS[next.instrument].defaultLength);
  };

  const onCellClick = (pitch, step) => {
    const adding = !noteAt(track, pitch, step);
    setSong(toggleNote(song, track.id, pitch, step, length));
    if (adding && !playing) preview(track.instrument, pitch);
  };

  return (
    <div>
      {/* Transport */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
        <button
          type="button"
          onClick={togglePlay}
          disabled={waking}
          className={cn(
            "flex h-11 min-w-[6.5rem] items-center justify-center gap-2 rounded-lg px-4 font-semibold transition-colors",
            playing
              ? "bg-zinc-100 text-zinc-900 hover:bg-white"
              : "bg-amber-300 text-zinc-900 hover:bg-amber-200",
            waking && "opacity-60"
          )}
        >
          {waking ? "Waking…" : playing ? "■ Stop" : "▶ Play"}
        </button>
        <span className="text-sm text-zinc-400">
          {song.bars} {song.bars === 1 ? "bar" : "bars"} ·{" "}
          {Math.round(loopSeconds(song))} sec · loop
        </span>
        {showTempo && (
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            Tempo
            <input
              type="range"
              min="50"
              max="110"
              value={song.bpm}
              onChange={(e) => setSong(setBpm(song, Number(e.target.value)))}
              className="w-28 accent-amber-300"
            />
            <span className="w-16 tabular-nums text-zinc-200">
              {song.bpm} bpm
            </span>
          </label>
        )}
        {transportExtras && (
          <div className="ml-auto flex flex-wrap gap-2">{transportExtras}</div>
        )}
      </div>
      {error && <p className="mb-4 text-sm text-rose-300">{error}</p>}

      {beforeLanes}

      <LaneList
        tracks={song.tracks}
        steps={steps}
        selectedId={track.id}
        onSelect={selectLane}
        onToggleMute={(id) =>
          setSong(
            setMuted(song, id, !song.tracks.find((t) => t.id === id).muted)
          )
        }
        onClear={(id) => {
          const lane = song.tracks.find((t) => t.id === id);
          const name = INSTRUMENTS[lane.instrument].name.toLowerCase();
          if (window.confirm(`Clear every note in the ${name} lane?`)) {
            setSong(clearTrack(song, id));
          }
        }}
        registerPlayhead={registerPlayhead}
      />

      <div className="mt-6 mb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{inst.name}</h2>
          <p className="text-sm text-zinc-500">{inst.blurb}</p>
        </div>
        {!inst.unpitched && (
          <div
            className="flex flex-wrap items-center gap-1 rounded-lg border border-zinc-800 p-1"
            role="group"
            aria-label="Note length"
          >
            <span className="px-2 text-xs text-zinc-500">note length</span>
            {lengths.map((l) => (
              <button
                key={l.steps}
                type="button"
                onClick={() => setLength(l.steps)}
                aria-pressed={length === l.steps}
                className={cn(
                  "rounded-md px-2.5 py-1 text-sm",
                  length === l.steps
                    ? "bg-zinc-100 text-zinc-900"
                    : "text-zinc-400 hover:bg-zinc-800"
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {soundControls.length > 0 && (
        <div className="mb-3">
          <SoundPanel
            track={track}
            controls={soundControls}
            onChange={(patch) => setSong(setSound(song, track.id, patch))}
          />
        </div>
      )}
      <PianoRoll
        rows={trackRows(song, track)}
        notes={track.notes}
        steps={steps}
        stepsPerBar={song.stepsPerBar}
        noteClass={LANE_STYLES[track.instrument].note}
        onCellClick={onCellClick}
        onResize={
          inst.unpitched
            ? null
            : (pitch, step, length) =>
                setSong(setNoteLength(song, track.id, pitch, step, length))
        }
        playheadRef={registerPlayhead}
      />
      <p className="mt-2 text-xs text-zinc-500">
        {inst.unpitched
          ? "Click a square to add a hit, click it again to remove it. Thick lines mark each bar; every other square is a beat."
          : "Click a square to place a note, click a note to remove it, drag a note's right edge to change its length."}{" "}
        Space bar plays and stops.
      </p>
    </div>
  );
}
